import React, { useMemo, useState } from "react";
import { Modal } from "react-bootstrap";

import HttpManager from "../../../../services/HttpManager";
import { useUnaALaVez } from "../../../../utils/unaALaVez";
import horarioBonito from "../../../../utils/horarioBonito";
import { FechaDMY, FechaHoraDMY, HoraAmPm } from "../../../../components/fechas/camposFecha";
import { horarioAmPm } from "../../../../utils/fechaHoraAmPm";
import RegalosPromo, { Alternativa, faltanRegalos, giftOptionsPayload, nombreAlternativa, regalosDePromo } from "./regalosPromo";
import { serverError } from "../../../../components/modals/payments/register/registerPaymentModal";

/*
	Catálogo de Promos (Módulos Claros, 2026-11-01): el editor va en una ventana, con «Así Queda» fijo arriba y Guardar fijo abajo, y solo los
	campos del tipo elegido. Lo que ninguna promo usaba en producción ya no sale, salvo en la promo que ya lo tiene (opcionesViejas). Lo que
	se guarda es exactamente lo de antes (armarPayload es el mismo cálculo). Sin textos explicativos.
	Editor de promociones (antes Descuentos › Catálogo). Rediseño 2026-10-27 (Esteban: «confuso, tough to do y no flexible enough»):
	1 se empieza por el TIPO de promoción (tarjetas) y solo se ven los campos de ese tipo · 2 una frase arriba resume lo que se está
	armando («Da $500 menos en la 1ra mensualidad · a nuevos referidos · en todos los cursos · hasta el 31/12 · la da la asesora») ·
	3 «Se combina con» en POSITIVO: lo que las reglas ya deciden se dice en texto (dos de mensualidades nunca van juntas; la de inscripción
	va con todas) y solo se marca con qué otras SÍ se junta, con Todas / Ninguna · 4 Todas / Ninguna en cada lista de casillas ·
	5 cupos, franja, días y quién la da quedan en «Más Opciones» · 6 el tipo Referido crea las DOS mitades de un jalón (la promo del
	alumno nuevo «Referido por» y el beneficio de quien lo refirió «Refiere»). El servidor guarda los mismos campos de siempre y valida todo.
	Teléfono: una columna, casillas y botones de 44 px, letra de 16 px.
*/

const http = HttpManager.getInstance();
type Promo = any;

export const GRUPOS: Record<string, string> = { mensualidad: "Mensualidades", inscripcion: "Inscripción / Matrícula", referido: "Para Quien Refiere", producto: "Kits y Productos" };
export const VINCULOS: Record<string, string> = { segundo: "2da Persona / 2do Curso", grupo: "Grupo", dos_x_uno: "2x1 (curso base)", referido_por: "Referido por", refiere: "Refiere a Otro" };
export const AUDIENCIAS: Record<string, string> = { nuevo: "Solo Nuevos", reingreso: "Solo Reingresos", actual: "Alumnos Actuales (2do curso)", graduado: "Solo Graduados" };
const DIAS: [number, string][] = [[1, "Lu"], [2, "Ma"], [3, "Mi"], [4, "Ju"], [5, "Vi"], [6, "Sá"], [7, "Do"]];
/** "2032-09-01" → "01/09/2032". */
const dmy = (d: string | null | undefined) => (d && /^\d{4}-\d{2}-\d{2}/.test(d) ? d.slice(8, 10) + "/" + d.slice(5, 7) + "/" + d.slice(0, 4) : d || "");
const PERIODOS: Record<string, string> = { dia: "Por Día", semana: "Por Semana", mes: "Por Mes", anio: "Por Año", total: "En Total" };
const num = (n: number) => (Math.round((Number(n) || 0) * 100) / 100).toLocaleString("es-MX", { maximumFractionDigits: 2 });

export type Tipo = "mensualidad" | "inscripcion" | "referido" | "segundo" | "grupo" | "dos_x_uno" | "producto";
export const TIPOS: { id: Tipo; label: string; sub: string }[] = [
	{ id: "mensualidad", label: "Mensualidades", sub: "Porcentaje, monto, precio fijo o meses regalados" },
	{ id: "inscripcion", label: "Inscripción / Matrícula", sub: "Gratis o con descuento; va con cualquier otra" },
	{ id: "referido", label: "Referido", sub: "Al alumno nuevo y a quien lo refirió" },
	{ id: "segundo", label: "2da Persona / 2do Curso", sub: "Ligada a otra matrícula (la base paga completo)" },
	{ id: "grupo", label: "Grupo", sub: "Varias personas inscritas juntas" },
	{ id: "dos_x_uno", label: "2x1", sub: "Un curso base pagado, el otro regalado" },
	{ id: "producto", label: "Kits y Productos", sub: "La aplica Caja al cobrar" }
];
export const TIPO_NOMBRE: Record<Tipo, string> = { mensualidad: "Mensualidades", inscripcion: "Inscripción", referido: "Referido", segundo: "2da Persona / 2do Curso", grupo: "Grupo", dos_x_uno: "2x1", producto: "Kits y Productos" };

/** Qué tipo es una promo guardada (para abrirla en el editor con sus campos). */
export const tipoDe = (p: Promo | null): Tipo => {
	if (!p) return "mensualidad";
	if (p.group === "referido" || p.linkType === "referido_por") return "referido";
	if (p.group === "producto") return "producto";
	if (p.group === "inscripcion") return "inscripcion";
	if (p.linkType === "segundo" || p.linkType === "grupo" || p.linkType === "dos_x_uno") return p.linkType;
	return "mensualidad";
};

export type Draft = {
	id?: number; name: string; tipo: Tipo; que: string; value: string; monthsPaid: string; monthsFree: string; monthFrom: string; monthTo: string;
	/** Referido: «nuevo» = la promo del alumno nuevo · «refiere» = el beneficio de quien refirió · «ambas» = promo nueva, crea las dos. */
	mitad: "nuevo" | "refiere" | "ambas"; queRefiere: "percent" | "amount"; valueRefiere: string;
	requirePaidMonths: string; allMonthsInPayment: boolean; freeSecond: boolean; minMonthsInPayment: string;
	/** 50 %: «curso» = solo 2do curso de la misma persona · «persona» = solo otra persona · "" = los dos. */
	secondFor: string; freeFirst: string; subscriptionCharge: string;
	cursosModo: "todos" | "estos" | "menos"; courseIds: number[]; levelIds: number[]; excludeCourseIds: number[]; excludeLevelIds: number[];
	talleres: boolean; excludeTalleres: boolean;
	scheduleIds: number[]; periodIds: number[]; modality: string; monthsTotal: string; periodFrom: string;
	audience: string; groupSize: string;
	/** Solo reingresos: meses completos fuera desde su último mes cursado o pagado (vacío = sin límite). */
	monthsOutMin: string; monthsOutMax: string;
	startsAt: string; endsAt: string; weekdays: number[]; timeFrom: string; timeTo: string;
	quotaLimit: string; quotaPeriod: string; quotaScope: string; quotaKey: string; quotaPerAdvisor: string;
	givenBy: string; requiresApproval: boolean; notCombinable: string[]; active: boolean; conditions: string; reason: string;
	/** Regalos (ESPEC_PROMO_REGALOS): alternativas de productos del catálogo; la variante se elige en la entrega. */
	regalos: Alternativa[];
};

/** « con 3 a 8 meses fuera», « con 24 meses o más fuera»; "" sin límites. Igual que AcuerdosService::textoMesesFuera. */
const textoMesesFuera = (min: string, max: string): string => {
	const a = min !== "" ? Number(min) : null, b = max !== "" ? Number(max) : null;
	if (a === null && b === null) return "";
	const t = a !== null && b !== null ? (a === b ? a + " meses" : a + " a " + b + " meses") : a !== null ? a + " meses o más" : "hasta " + b + " meses";
	return " con " + t + " fuera (desde su último mes cursado o pagado)";
};

export const toDraft = (p: Promo | null): Draft => {
	const r = (p && p.rules) || {};
	const multimes = !!(r.monthsPaid || r.freeLast);
	const tipo = tipoDe(p);
	return {
		id: p?.id, name: p?.name || "", tipo,
		que: multimes ? "multimes" : (p?.calcType || "percent"), value: p ? String(p.value) : "",
		mitad: p ? (p.group === "referido" ? "refiere" : "nuevo") : "ambas", queRefiere: "amount", valueRefiere: "",
		monthsPaid: r.monthsPaid ? String(r.monthsPaid) : (r.monthsTotal && r.freeLast ? String(r.monthsTotal - r.freeLast) : ""),
		monthsFree: r.monthsFree ? String(r.monthsFree) : (r.freeLast ? String(r.freeLast) : ""),
		monthFrom: r.monthFrom ? String(r.monthFrom) : "", monthTo: r.monthTo ? String(r.monthTo) : "", requirePaidMonths: r.requirePaidMonths ? String(r.requirePaidMonths) : "",
		allMonthsInPayment: !!r.allMonthsInPayment, freeSecond: !!r.freeSecond, minMonthsInPayment: r.minMonthsInPayment ? String(r.minMonthsInPayment) : "",
		secondFor: r.secondFor || "", freeFirst: r.freeFirst ? String(r.freeFirst) : "", subscriptionCharge: r.subscriptionCharge ? String(r.subscriptionCharge) : "",
		cursosModo: (r.courseIds || []).length || (r.levelIds || []).length ? "estos" : ((r.excludeCourseIds || []).length || (r.excludeLevelIds || []).length ? "menos" : "todos"),
		courseIds: r.courseIds || [], levelIds: r.levelIds || [], excludeCourseIds: r.excludeCourseIds || [], excludeLevelIds: r.excludeLevelIds || [],
		talleres: !!r.talleres, excludeTalleres: !!r.excludeTalleres,
		scheduleIds: r.scheduleIds || [], periodIds: r.periodIds || [], modality: r.modality || "", monthsTotal: !multimes && r.monthsTotal ? String(r.monthsTotal) : "",
		periodFrom: r.periodFrom || "", audience: p?.audience || "", groupSize: p?.groupSize ? String(p.groupSize) : "",
		monthsOutMin: r.monthsOutMin != null ? String(r.monthsOutMin) : "", monthsOutMax: r.monthsOutMax != null ? String(r.monthsOutMax) : "",
		startsAt: (p?.startsAt || "").replace(" ", "T"), endsAt: (p?.endsAt || "").replace(" ", "T"), weekdays: p?.weekdays || [], timeFrom: p?.timeFrom || "", timeTo: p?.timeTo || "",
		quotaLimit: p?.quotaLimit ? String(p.quotaLimit) : "", quotaPeriod: p?.quotaPeriod || "", quotaScope: p?.quotaScope || "plantel", quotaKey: p?.quotaKey || "",
		quotaPerAdvisor: p?.quotaPerAdvisor ? String(p.quotaPerAdvisor) : "", givenBy: p?.givenBy || (p?.scope === "caja" ? "caja" : "asesora"),
		requiresApproval: !!p?.requiresApproval, notCombinable: p?.notCombinable || [], active: p ? !!p.active : true, conditions: p?.conditions || "", reason: "",
		regalos: regalosDePromo(p)
	};
};

/** Lo que cada tipo significa para el servidor (grupo de combinación, «¿con quién?» y quién la da). */
const datosDeTipo = (d: Draft): { group: string; linkType: string | null; givenBy: string } => {
	switch (d.tipo) {
		case "inscripcion": return { group: "inscripcion", linkType: null, givenBy: d.givenBy };
		case "producto": return { group: "producto", linkType: null, givenBy: "caja" };
		case "segundo": case "grupo": case "dos_x_uno": return { group: "mensualidad", linkType: d.tipo, givenBy: d.givenBy };
		case "referido": return d.mitad === "refiere" ? { group: "referido", linkType: "refiere", givenBy: d.givenBy } : { group: "mensualidad", linkType: "referido_por", givenBy: d.givenBy };
		default: return { group: "mensualidad", linkType: null, givenBy: d.givenBy };
	}
};

/** Casillas con Todas / Ninguna (Esteban, 2026-10-27: «no hay un select all»). */
export const Casillas: React.FC<{ opciones: { id: any; label: string; sub?: string }[]; elegidos: any[]; onChange: (v: any[]) => void; disabled?: boolean; alto?: number; todo?: boolean }> =
	({ opciones, elegidos, onChange, disabled, alto, todo }) => (
		<div className="wdCasillas" style={{ maxHeight: alto || 220 }}>
			{todo && opciones.length > 1 ? (
				<div className="wdCasillasCab">
					<button type="button" className="wdLink" disabled={disabled} onClick={() => onChange(Array.from(new Set([...elegidos, ...opciones.map((o) => o.id)])))}>Todas</button>
					<button type="button" className="wdLink" disabled={disabled} onClick={() => onChange(elegidos.filter((x) => !opciones.some((o) => o.id === x)))}>Ninguna</button>
					<span className="wdTenue">{opciones.filter((o) => elegidos.includes(o.id)).length} de {opciones.length}</span>
				</div>
			) : null}
			{opciones.length ? opciones.map((o) => (
				<label key={"c" + o.id} className="wdCasilla">
					<input type="checkbox" checked={elegidos.includes(o.id)} disabled={disabled}
						onChange={(e) => onChange(e.target.checked ? [...elegidos, o.id] : elegidos.filter((x) => x !== o.id))} />
					<span>{o.label}{o.sub ? <small> · {o.sub}</small> : null}</span>
				</label>
			)) : <div className="wdTenue" style={{ fontSize: 13, padding: 6 }}>Nada que elegir.</div>}
		</div>
	);

const ampm = (hhmm: string) => { const [h, m] = hhmm.split(":").map(Number); return (h % 12 === 0 ? 12 : h % 12) + ":" + String(m).padStart(2, "0") + (h < 12 ? " am" : " pm"); };
const fechaHora = (v: string) => { if (!v) return ""; const [f, h] = v.split("T"); return dmy(f) + (h && h !== "00:00" ? " " + ampm(h.slice(0, 5)) : ""); };

/** Lo que da la promo, en palabras («20 %», «$500 menos», «precio fijo $1,000», «paga 6 y regala 2», «gratis»). */
const valorEnPalabras = (que: string, value: string, monthsPaid: string, monthsFree: string, sym: string): string => {
	if (que === "multimes") return Number(monthsPaid) > 0 && Number(monthsFree) > 0 ? "paga " + monthsPaid + " y regala " + monthsFree : "meses regalados (falta cuántos)";
	const v = Number(value) || 0;
	if (v <= 0) return "… (falta el valor)";
	if (que === "percent") return v >= 100 ? "gratis" : num(v) + " %";
	if (que === "fixed_price") return "precio fijo " + sym + num(v);
	return sym + num(v) + " menos";
};

/**
 * Promo de adelanto (igual que PromocionAdelantoService::kind): meses regalados, kit (de producto y pide N mensualidades pagadas), 2x1 o
 * «todas juntas». Solo esas pueden regalar productos.
 */
/** «Pide N mensualidades pagadas» de verdad: «all» o un número mayor que 0 (igual que el servidor; "0" o vacío no cuenta). */
const pideMensualidades = (v: string): boolean => v.trim().toLowerCase() === "all" || Number(v) > 0;
const esDeAdelanto = (d: Draft): boolean => {
	const grupoMens = d.tipo === "mensualidad" || d.tipo === "segundo" || d.tipo === "grupo" || d.tipo === "dos_x_uno" || (d.tipo === "referido" && d.mitad !== "refiere");
	return (grupoMens && d.que === "multimes") || (d.tipo === "producto" && pideMensualidades(d.requirePaidMonths)) || d.tipo === "dos_x_uno" || (d.tipo === "mensualidad" && d.allMonthsInPayment);
};

/** La frase que resume la promo mientras se arma (si se lee bien, está bien). */
export const frase = (d: Draft, sym: string, cursos: any[]): string => {
	const partes: string[] = [];
	const valor = valorEnPalabras(d.que, d.value, d.monthsPaid, d.monthsFree, sym);
	const meses = d.que === "multimes" ? "" : d.monthFrom && d.monthTo ? (d.monthFrom === d.monthTo ? " en la mensualidad " + d.monthFrom : " de la mensualidad " + d.monthFrom + " a la " + d.monthTo)
		: d.monthFrom ? " desde la mensualidad " + d.monthFrom : d.monthTo ? " hasta la mensualidad " + d.monthTo : " en todas las mensualidades";
	if (d.tipo === "inscripcion") partes.push("Da " + valor + " en la inscripción");
	else if (d.tipo === "producto") partes.push("Da " + valor + " en kits y productos" + (d.requirePaidMonths ? " con " + d.requirePaidMonths + " mensualidades pagadas" : ""));
	else if (d.tipo === "referido" && d.mitad === "refiere") partes.push("Da " + valor + " a quien refirió, en su siguiente mensualidad, cuando el referido paga su primera");
	else if (d.tipo === "referido") {
		const nuevo = Number(d.value) > 0 || d.mitad === "nuevo" ? "Da " + valor + meses + " al alumno referido" : "";
		const refiere = d.mitad === "ambas" && Number(d.valueRefiere) > 0 ? valorEnPalabras(d.queRefiere, d.valueRefiere, "", "", sym) + " a quien lo refirió (en su siguiente mensualidad, cuando el nuevo paga su primera)" : "";
		partes.push([nuevo, refiere].filter(Boolean).join(" y ") || "Da … (falta el valor)");
	} else partes.push("Da " + valor + meses + (d.que === "multimes" ? " (solo cursos de " + (Number(d.monthsPaid) + Number(d.monthsFree) || "N+M") + " meses)" : ""));
	if (esDeAdelanto(d) && d.regalos.length) {
		const nombres = d.regalos.map((a) => "«" + nombreAlternativa(a) + "»");
		partes[0] += (d.tipo === "producto" ? ": regala " : " y regala ") + (nombres.length > 1 ? nombres.slice(0, -1).join(", ") + " o " + nombres[nombres.length - 1] + " (Caja elige)" : nombres[0]);
	}
	if (d.tipo === "segundo") {
		const n = Number(d.freeFirst);
		if (n > 0) partes[0] = "Deja " + (n === 1 ? "la 1ra mensualidad" : "las primeras " + n + " mensualidades") + " en $0 y da " + valor + " desde la mensualidad " + (n + 1);
		partes.push(d.secondFor === "persona" ? "a la 2da persona (ligada a otra persona)" : d.secondFor === "curso" ? "al 2do curso de la misma persona" : "a la 2da persona o el 2do curso (ligado a otra matrícula)");
		if (Number(d.subscriptionCharge) > 0) partes.push("cobrándole la inscripción de " + sym + num(Number(d.subscriptionCharge)));
	}
	else if (d.tipo === "grupo") partes.push("a cada persona de un grupo de " + (d.groupSize || "…"));
	else if (d.tipo === "dos_x_uno") partes.push("al curso regalado del 2x1");
	if (d.audience) partes.push(d.audience === "nuevo" ? "solo a alumnos nuevos" : d.audience === "reingreso" ? "solo a reingresos" + textoMesesFuera(d.monthsOutMin, d.monthsOutMax) : d.audience === "graduado" ? "solo a graduados de la academia" : "solo a alumnos actuales (2do curso)");
	if (d.tipo !== "referido" || d.mitad !== "refiere") {
		const n = d.cursosModo === "estos" ? d.courseIds.length + d.levelIds.length : d.excludeCourseIds.length + d.excludeLevelIds.length;
		const nombre = (ids: number[]) => ids.length === 1 ? (cursos.find((c) => c.id === ids[0])?.name || "1 curso") : ids.length + " cursos";
		partes.push(d.cursosModo === "todos" ? "en todos los cursos" : d.cursosModo === "estos" ? (n ? "solo en " + nombre(d.courseIds) + (d.levelIds.length ? " y " + d.levelIds.length + " módulos" : "") : "solo en … (faltan cursos)")
			: (n ? "en todos los cursos menos " + nombre(d.excludeCourseIds) : "en todos menos … (faltan cursos)"));
		if (d.modality) partes.push(d.modality === "online" ? "solo en línea" : "solo presencial");
		if (d.scheduleIds.length) partes.push("solo " + d.scheduleIds.length + (d.scheduleIds.length === 1 ? " horario" : " horarios"));
		if (d.periodIds.length) partes.push("solo " + d.periodIds.length + (d.periodIds.length === 1 ? " periodo" : " periodos"));
	}
	if (d.startsAt || d.endsAt) partes.push((d.startsAt ? "desde el " + fechaHora(d.startsAt) : "") + (d.startsAt && d.endsAt ? " " : "") + (d.endsAt ? "hasta el " + fechaHora(d.endsAt) : ""));
	else partes.push("estable (sin fecha de fin)");
	if (d.weekdays.length && d.weekdays.length < 7) partes.push("solo " + d.weekdays.map((n) => ["", "lunes", "martes", "miércoles", "jueves", "viernes", "sábados", "domingos"][n]).join(", "));
	if (d.timeFrom && d.timeTo) partes.push("de " + ampm(d.timeFrom) + " a " + ampm(d.timeTo));
	if (d.quotaLimit) partes.push("máximo " + d.quotaLimit + " usos " + (PERIODOS[d.quotaPeriod] || "").toLowerCase());
	const da = datosDeTipo(d).givenBy;
	partes.push(da === "caja" ? "la aplica Caja al cobrar" : da === "supervisora" ? "solo la da la supervisora" : "la da la asesora al matricular");
	if (d.requiresApproval) partes.push("con aprobación");
	if (!d.active) partes.push("desactivada");
	return partes.join(" · ");
};

/** Lo que sale del tipo y del borrador (lo mismo que calculaba el editor de antes, ahora fuera para poder armar el envío sin la ventana). */
const derivados = (d: Draft, promos: Promo[]) => {
	const datos = datosDeTipo(d);
	const esRefiere = d.tipo === "referido" && d.mitad === "refiere";
	const conMeses = datos.group === "mensualidad" && d.que !== "multimes";
	const esKit = d.tipo === "producto" && pideMensualidades(d.requirePaidMonths);
	const conRegalos = esDeAdelanto(d);
	// «Se combina con», en positivo: marcada = se junta; sin marcar = va a la lista «no se combina con» del servidor (la de siempre).
	const otras = (promos || []).filter((p) => p.id !== d.id && p.group !== "inscripcion" && !(datos.group === "mensualidad" && p.group === "mensualidad") && !(datos.group === "referido" && p.group === "referido"));
	const noComb = d.notCombinable.map((c) => String(c).toUpperCase());
	return { datos, esRefiere, conMeses, esKit, conRegalos, otras, noComb };
};

/** Lo que se manda al servidor: idéntico al editor de antes (mismos campos, mismos valores). */
export const armarPayload = (d: Draft, promos: Promo[], over: Partial<{ group: string; linkType: string | null; calcType: string; value: number; name: string; rules: any }>) => {
	const { datos, conMeses, esKit, conRegalos, otras, noComb } = derivados(d, promos);
	const multimes = d.que === "multimes";
	const rules: any = {
		monthFrom: conMeses ? d.monthFrom : "", monthTo: conMeses ? d.monthTo : "", periodFrom: d.periodFrom, allMonthsInPayment: d.allMonthsInPayment, freeSecond: d.freeSecond,
		requirePaidMonths: d.tipo === "producto" ? d.requirePaidMonths : "", minMonthsInPayment: d.minMonthsInPayment, modality: d.modality || null,
		monthsTotal: multimes ? "" : d.monthsTotal, monthsPaid: multimes ? d.monthsPaid : "", monthsFree: multimes ? d.monthsFree : "",
		// [0] = «ningún curso» (lo dejó la migración): se manda tal cual mientras no se elija otro curso; nunca se vuelve «todos».
		courseIds: d.cursosModo === "estos" ? (d.courseIds.some((x) => x > 0) ? d.courseIds.filter((x) => x > 0) : d.courseIds) : [],
		levelIds: d.cursosModo === "estos" ? d.levelIds : [],
		excludeCourseIds: d.cursosModo === "menos" ? d.excludeCourseIds : [], excludeLevelIds: d.cursosModo === "menos" ? d.excludeLevelIds : [],
		talleres: d.cursosModo === "estos" && d.talleres, excludeTalleres: d.cursosModo === "menos" && d.excludeTalleres,
		scheduleIds: d.scheduleIds, periodIds: d.periodIds,
		monthsOutMin: d.audience === "reingreso" ? d.monthsOutMin : "", monthsOutMax: d.audience === "reingreso" ? d.monthsOutMax : "",
		secondFor: d.tipo === "segundo" ? d.secondFor : "", freeFirst: d.tipo === "segundo" || d.tipo === "mensualidad" ? d.freeFirst : "",
		subscriptionCharge: d.tipo === "segundo" ? d.subscriptionCharge : ""
	};
	return {
		id: d.id, name: d.name, group: datos.group, scope: datos.givenBy === "caja" ? "caja" : "acuerdo", calcType: multimes ? "percent" : d.que,
		value: multimes ? 100 : Number(d.value), audience: d.audience || null, linkType: datos.linkType, groupSize: d.tipo === "grupo" && d.groupSize ? Number(d.groupSize) : null,
		startsAt: d.startsAt || null, endsAt: d.endsAt || null, weekdays: d.weekdays, timeFrom: d.timeFrom || null, timeTo: d.timeTo || null,
		quotaLimit: d.quotaLimit || null, quotaPeriod: d.quotaPeriod || null, quotaScope: d.quotaScope, quotaKey: d.quotaKey || null, quotaPerAdvisor: d.quotaPerAdvisor || null,
		givenBy: datos.givenBy, requiresApproval: d.requiresApproval, notCombinable: otras.filter((p) => noComb.includes(String(p.code).toUpperCase())).map((p) => p.code),
		active: d.active, conditions: d.conditions, reason: d.reason, rules,
		// giftArticleId queda por compatibilidad (= el 1er producto de la 1ra alternativa de una promo de kit); manda giftOptions.
		giftArticleId: esKit && d.regalos[0]?.items[0] ? d.regalos[0].items[0].articleId : null,
		giftOptions: conRegalos ? giftOptionsPayload(d.regalos) : null, ...over
	};
};

/** Desactivar / Activar desde la ventana: lo mismo que abrir el editor, cambiar «Activa» y guardar con su motivo. */
export const payloadActivar = (promo: Promo, promos: Promo[], active: boolean, reason: string) =>
	armarPayload({ ...toDraft(promo), active, reason }, promos, {});

/**
 * Opciones que ninguna promo usa en producción (SELECT 1-nov-2026, 108 promos: 0 cupos, 0 días, 0 franja, 0 aprobación, 0 modalidad,
 * 0 periodos, 0 horarios, 0 módulos sueltos, 0 «Solo Supervisora»). Se enseñan solo si la promo que se edita ya trae alguna.
 */
const opcionesViejas = (p: Promo | null) => {
	const r = (p && p.rules) || {};
	return {
		cupos: !!p && (!!p.quotaLimit || !!p.quotaPeriod || !!p.quotaPerAdvisor || (!!p.quotaScope && p.quotaScope !== "plantel")),
		dias: !!p && (p.weekdays || []).length > 0,
		franja: !!p && (!!p.timeFrom || !!p.timeTo),
		aprobacion: !!p && !!p.requiresApproval,
		modalidad: !!r.modality,
		periodos: (r.periodIds || []).length > 0,
		horarios: (r.scheduleIds || []).length > 0,
		modulos: (r.levelIds || []).length > 0 || (r.excludeLevelIds || []).length > 0,
		supervisora: !!p && p.givenBy === "supervisora"
	};
};

const titulo = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

export const EditorPromo: React.FC<{ campusId: number; promo: Promo | null; cursos: any; promos: Promo[]; sym: string; onSaved: () => void; onCancel: () => void }> =
	({ campusId, promo, cursos, promos, sym, onSaved, onCancel }) => {
		const [d, setD] = useState<Draft>(toDraft(promo));
		const [error, setError] = useState<string | null>(null);
		const [busy, setBusy] = useState(false);
		const [buscar, setBuscar] = useState("");
		const [elegirComb, setElegirComb] = useState(false);
		const viejas = useMemo(() => opcionesViejas(promo), [promo]);
		const set = (k: keyof Draft, v: any) => setD((x) => ({ ...x, [k]: v }));
		const cursosLista = (cursos?.courses || []) as any[];
		const nombreModulo = useMemo(() => {
			const m: Record<number, string> = {};
			cursosLista.forEach((c) => (c.levels || []).forEach((l: any) => { m[l.id] = c.name + " · " + l.name; }));
			return m;
		}, [cursosLista]);
		const filtrados = cursosLista.filter((c) => !buscar.trim() || c.name.toLowerCase().includes(buscar.trim().toLowerCase()));
		const elegidosCurso = d.cursosModo === "menos" ? d.excludeCourseIds : d.courseIds;
		const horarios = ((cursos?.schedules || []) as any[]).filter((s) => d.cursosModo !== "estos" || !d.courseIds.length || d.courseIds.includes(s.courseId));
		const { datos, esRefiere, conMeses, esKit, conRegalos, otras, noComb } = derivados(d, promos);
		// Cursos que cubre la promo tal como está en el editor: los chips de cada alternativa solo ofrecen estos.
		const cursosPromo = useMemo(() => {
			if (d.cursosModo === "estos") {
				const ids = new Set(d.courseIds.filter((x) => x > 0));
				cursosLista.forEach((c) => { if ((c.levels || []).some((l: any) => d.levelIds.includes(l.id))) ids.add(c.id); });
				return cursosLista.filter((c) => ids.has(c.id)).map((c) => ({ id: Number(c.id), name: String(c.name) }));
			}
			const fuera = d.cursosModo === "menos" ? d.excludeCourseIds : [];
			return cursosLista.filter((c) => !fuera.includes(c.id)).map((c) => ({ id: Number(c.id), name: String(c.name) }));
		}, [cursosLista, d.cursosModo, d.courseIds, d.levelIds, d.excludeCourseIds]);
		// El plantel ya tiene su promo para quien refiere: una promo de Referido nueva solo crea la mitad del alumno nuevo.
		const refiereExistente = (promos || []).find((p) => p.linkType === "refiere" && p.active && p.id !== d.id) || null;

		const elegirTipo = (t: Tipo) => setD((x) => {
			const n: Draft = { ...x, tipo: t };
			if (t === "producto") { n.givenBy = "caja"; if (n.que === "multimes") n.que = "percent"; }
			else if (x.tipo === "producto") n.givenBy = "asesora";
			if (t === "inscripcion") { n.que = n.que === "multimes" ? "percent" : n.que; n.monthFrom = ""; n.monthTo = ""; }
			if (t === "referido" && !promo) { n.mitad = refiereExistente ? "nuevo" : "ambas"; if (!n.monthFrom && !n.monthTo) { n.monthFrom = "1"; n.monthTo = "1"; } if (!n.audience) n.audience = "nuevo"; if (n.que === "multimes") n.que = "amount"; }
			if (t === "grupo" && !n.groupSize) n.groupSize = "3";
			if (t === "dos_x_uno") { n.que = "percent"; n.value = "100"; }
			return n;
		});

		const combinables = otras.filter((p) => !noComb.includes(String(p.code).toUpperCase())).map((p) => p.code);
		const setCombinables = (codes: string[]) => set("notCombinable", otras.filter((p) => !codes.includes(p.code)).map((p) => p.code));
		const modoComb = combinables.length === otras.length ? "todas" : combinables.length === 0 ? "ninguna" : "algunas";

		const unaVez = useUnaALaVez();
		const save = () => unaVez(saveAhora);
		const payload = (over: Parameters<typeof armarPayload>[2]) => armarPayload(d, promos, over);
		const saveAhora = async () => {
			try {
				setBusy(true); setError(null);
				if (d.tipo === "referido" && d.mitad === "ambas") {
					// Las dos mitades de un jalón: la del alumno nuevo (si lleva valor) y la de quien refirió (si lleva valor).
					if (Number(d.value) > 0) {
						await http.paymentService.savePromotion(campusId, payload({ group: "mensualidad", linkType: "referido_por" }));
					}
					if (Number(d.valueRefiere) > 0) {
						// La mitad de quien refiere va sobre la matrícula que YA tiene: sin audiencia ni cursos (eso es de la mitad del nuevo).
						await http.paymentService.savePromotion(campusId, { ...payload({ group: "referido", linkType: "refiere", calcType: d.queRefiere, value: Number(d.valueRefiere),
							name: d.name + " · quien refiere", rules: { monthFrom: "", monthTo: "", courseIds: [], levelIds: [], excludeCourseIds: [], excludeLevelIds: [], scheduleIds: [], periodIds: [] } }), audience: null });
					}
				} else {
					await http.paymentService.savePromotion(campusId, payload({}));
				}
				onSaved();
			} catch (e) {
				setError(serverError(e));
			} finally {
				setBusy(false);
			}
		};
		const campo = (label: string, el: React.ReactNode) => <label className="wdCampo"><span>{label}</span>{el}</label>;
		// Campos de varias piezas (fecha + hora + am/pm): en un <label> el clic se iba al primer campo; van en un <div>.
		const campoVarios = (label: string, el: React.ReactNode) => <div className="wdCampo"><span>{label}</span>{el}</div>;
		const toggles = (opciones: [string, string][], valor: string, onPick: (v: string) => void) => (
			<div className="wdOpciones">
				{opciones.map(([k, l]) => <button key={k} type="button" className={"wdToggle" + (valor === k ? " wdToggleOn" : "")} onClick={() => onPick(k)}>{l}</button>)}
			</div>
		);
		// «Solo estos» / «todos menos estos» sin ningún curso elegido NO se guarda (se volvería «todos los cursos» sin querer).
		const cursosOk = esRefiere || d.cursosModo === "todos" || (d.cursosModo === "estos" ? d.courseIds.length + d.levelIds.length : d.excludeCourseIds.length + d.excludeLevelIds.length) > 0;
		const ningunCurso = d.cursosModo === "estos" && d.courseIds.length > 0 && d.courseIds.every((x) => x === 0) && !d.levelIds.length;
		// Lo que falta para guardar, junto al botón (corto).
		const faltan: string[] = [];
		if (d.name.trim().length < 3) faltan.push("Nombre");
		if (d.tipo === "referido" && d.mitad === "ambas") {
			if (!(Number(d.value) > 0) && !(Number(d.valueRefiere) > 0)) faltan.push("Valor");
		} else if (d.que === "multimes" ? !(Number(d.monthsPaid) > 0 && Number(d.monthsFree) > 0) : !(Number(d.value) > 0)) {
			faltan.push(d.que === "multimes" ? "Meses que Paga y Regala" : "Valor");
		}
		if (conRegalos) faltan.push(...faltanRegalos(d.regalos, esKit && d.active, cursosLista.length ? cursosPromo.map((c) => c.id) : null).map(titulo));
		if (d.tipo === "grupo" && !(Number(d.groupSize) >= 2 && Number(d.groupSize) <= 10)) faltan.push("Personas del Grupo (2 a 10)");
		if (!cursosOk) faltan.push("Cursos");
		if (d.timeFrom && d.timeTo && d.timeFrom === d.timeTo) faltan.push("Franja");
		const motivo = d.reason.trim().length;
		if (promo && motivo < 10) faltan.push("Por Qué Cambia (" + motivo + "/10)");
		const valido = faltan.length === 0;
		const campoValor = (que: string, value: string, setQue: (v: string) => void, setValue: (v: string) => void, conMultimes: boolean) => (
			<>
				{toggles([["percent", "Porcentaje"], ["amount", "Monto Menos"], ["fixed_price", "Precio Fijo por Mes"], ...(conMultimes ? [["multimes", "Meses Regalados"]] as [string, string][] : [])], que, setQue)}
				<div className="wdGrid" style={{ marginTop: 8 }}>
					{que === "multimes" ? (
						<>
							{campo("Paga (Meses)", <input className="form-control" type="number" min="1" value={d.monthsPaid} onChange={(e) => set("monthsPaid", e.target.value)} />)}
							{campo("Regala (Meses)", <input className="form-control" type="number" min="1" value={d.monthsFree} onChange={(e) => set("monthsFree", e.target.value)} />)}
						</>
					) : campo(que === "percent" ? "Porcentaje" : que === "fixed_price" ? "Precio por Mes (" + sym + ")" : "Monto Menos (" + sym + ")",
						<input className="form-control" type="number" min="0" step="0.01" value={value} onChange={(e) => setValue(e.target.value)} />)}
				</div>
			</>
		);
		const deMeses = (
			<div className="wdGrid" style={{ marginTop: 8 }}>
				{campo("Desde la Mensualidad", <input className="form-control" type="number" min="1" value={d.monthFrom} onChange={(e) => set("monthFrom", e.target.value)} />)}
				{campo("Hasta la Mensualidad", <input className="form-control" type="number" min="1" value={d.monthTo} onChange={(e) => set("monthTo", e.target.value)} />)}
			</div>
		);

		return (
			<Modal className="waModulo wpmVentana waDescuentos wcpEditor" show onHide={busy ? undefined : onCancel} size="lg" centered scrollable fullscreen="sm-down" backdrop="static">
				<Modal.Header closeButton={!busy}>
					<Modal.Title style={{ fontSize: 18, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
						<span>{promo ? "Cambiar «" + promo.name + "»" : "Agregar Promoción"}</span>
						{promo && promo.version > 1 ? <span className="wdChip wdChipFilo">Versión {promo.version}</span> : null}
						{promo && promo.liveAgreements > 0 ? <span className="wdChip wdChipFilo">{promo.liveAgreements === 1 ? "1 Acuerdo Vivo" : promo.liveAgreements + " Acuerdos Vivos"}</span> : null}
					</Modal.Title>
				</Modal.Header>
				<div className="wcpAsiQueda"><b>Así Queda</b> {frase(d, sym, cursosLista)}</div>
				<Modal.Body>
					<div className="wdTipos">
						{TIPOS.map((t) => (
							<button key={t.id} type="button" className={"wdTipo" + (d.tipo === t.id ? " wdTipoOn" : "")} onClick={() => elegirTipo(t.id)}><b>{t.label}</b></button>
						))}
					</div>
					{d.tipo === "referido" && promo ? <div style={{ marginTop: 6 }}><span className="wdChip wdChipFilo">Mitad: {d.mitad === "refiere" ? "Quien Refiere" : "Alumno Nuevo"}</span></div> : null}

					<div className="wdGrid" style={{ marginTop: 12 }}>
						{campo("Nombre", <input className="form-control" value={d.name} onChange={(e) => set("name", e.target.value)} />)}
					</div>

					{d.tipo !== "dos_x_uno" ? <h6 className="wdSub">Qué Da</h6> : null}
					{d.tipo === "referido" && d.mitad === "ambas" ? (
						<>
							<div className="wcpMitad">Al Alumno Nuevo</div>
							{campoValor(d.que, d.value, (v) => set("que", v), (v) => set("value", v), false)}
							{deMeses}
							{!refiereExistente ? (
								<>
									<div className="wcpMitad" style={{ marginTop: 12 }}>A Quien Lo Refirió</div>
									{campoValor(d.queRefiere, d.valueRefiere, (v) => set("queRefiere", v === "fixed_price" ? "amount" : v), (v) => set("valueRefiere", v), false)}
								</>
							) : null}
						</>
					) : d.tipo === "dos_x_uno" ? null : (
						<>
							{campoValor(d.que, d.value, (v) => set("que", v), (v) => set("value", v), datos.group === "mensualidad" && !esRefiere)}
							{conMeses && !esRefiere ? deMeses : null}
							{d.tipo === "producto" ? <div className="wdGrid" style={{ marginTop: 8 }}>{campo("Mensualidades Pagadas que Pide", <input className="form-control" value={d.requirePaidMonths} onChange={(e) => set("requirePaidMonths", e.target.value)} placeholder="8 o all" />)}</div> : null}
							{d.tipo === "mensualidad" ? (
								<div className="wdOpciones" style={{ marginTop: 8 }}>
									<label className="wdCasilla"><input type="checkbox" checked={d.allMonthsInPayment} onChange={(e) => set("allMonthsInPayment", e.target.checked)} /><span>Solo Pagando Todas Juntas</span></label>
									<label className="wdCasilla"><input type="checkbox" checked={d.freeSecond} onChange={(e) => set("freeSecond", e.target.checked)} /><span>Paga 1, el 2do Gratis</span></label>
								</div>
							) : null}
						</>
					)}
					{d.tipo === "segundo" ? (
						<>
							<div style={{ marginTop: 8 }}>{toggles([["", "2da Persona o 2do Curso"], ["persona", "Solo 2da Persona"], ["curso", "Solo 2do Curso"]], d.secondFor, (v) => set("secondFor", v))}</div>
							<div className="wdGrid" style={{ marginTop: 8 }}>
								{campo("Mensualidades en $0 al Inicio", <input className="form-control" type="number" min="1" value={d.freeFirst} onChange={(e) => set("freeFirst", e.target.value)} />)}
								{campo("Inscripción que se Cobra", <input className="form-control" type="number" min="0" value={d.subscriptionCharge} onChange={(e) => set("subscriptionCharge", e.target.value)} />)}
							</div>
						</>
					) : null}
					{d.tipo === "grupo" ? <div className="wdGrid" style={{ marginTop: 8 }}>{campo("Personas del Grupo", <input className="form-control" type="number" min="2" max="10" value={d.groupSize} onChange={(e) => set("groupSize", e.target.value)} />)}</div> : null}

					{conRegalos ? (
						<>
							<h6 className="wdSub">Regalos</h6>
							<RegalosPromo campusId={campusId} sym={sym} promotionId={d.id || null} value={d.regalos} onChange={(v) => set("regalos", v)}
								cursosPromo={cursosPromo} cursosPlantel={cursosLista.map((c) => ({ id: Number(c.id), name: String(c.name) }))}
								cursosParaSugerir={cursosPromo.map((c) => c.id)} requerido={esKit} />
						</>
					) : null}

					{!esRefiere ? (
						<>
							<h6 className="wdSub">Cursos</h6>
							{toggles([["todos", "Todos los Cursos"], ["estos", "Solo Estos Cursos"], ["menos", "Todos Menos Estos"]], d.cursosModo, (v) => set("cursosModo", v))}
							{d.cursosModo !== "todos" ? (
								<>
									<input className="form-control" style={{ marginTop: 6 }} placeholder="Buscar Curso" value={buscar} onChange={(e) => setBuscar(e.target.value)} />
									<Casillas todo opciones={filtrados.map((c) => ({ id: c.id, label: c.name, sub: [c.modality === "online" ? "En Línea" : "Presencial", c.months?.length ? c.months.join(", ") + (c.months.length === 1 && c.months[0] === 1 ? " Mes" : " Meses") : ""].filter(Boolean).join(" · ") }))}
										elegidos={elegidosCurso} onChange={(v) => set(d.cursosModo === "menos" ? "excludeCourseIds" : "courseIds", v.filter((x: number) => x !== 0))} />
									{ningunCurso ? <div className="wdAviso" style={{ fontSize: 13 }}>No aplica a ningún curso.</div> : null}
									<label className="wdCasilla"><input type="checkbox" checked={d.cursosModo === "menos" ? d.excludeTalleres : d.talleres}
										onChange={(e) => set(d.cursosModo === "menos" ? "excludeTalleres" : "talleres", e.target.checked)} /><span>Y Sus Talleres</span></label>
									{viejas.modulos && (d.cursosModo === "estos" ? d.levelIds : d.excludeLevelIds).length ? (
										<div className="wdChips">
											<span className="wdTenue" style={{ fontSize: 13 }}>Módulos:</span>
											{(d.cursosModo === "estos" ? d.levelIds : d.excludeLevelIds).map((id) => (
												<button key={"l" + id} type="button" className="wdChip wdChipFilo wdChipBoton" title="Quitar"
													onClick={() => set(d.cursosModo === "estos" ? "levelIds" : "excludeLevelIds", (d.cursosModo === "estos" ? d.levelIds : d.excludeLevelIds).filter((x) => x !== id))}>
													{nombreModulo[id] || "Módulo " + id} ✕</button>
											))}
										</div>
									) : null}
								</>
							) : null}
							<div className="wdGrid" style={{ marginTop: 8 }}>
								{d.que !== "multimes" ? campo("Solo Cursos de N Meses", <input className="form-control" type="number" min="1" value={d.monthsTotal} onChange={(e) => set("monthsTotal", e.target.value)} />) : null}
								{campoVarios("Horarios del Período Desde", <FechaDMY value={d.periodFrom} onChange={(v) => set("periodFrom", v)} />)}
								{viejas.modalidad ? campo("Modalidad", <select className="form-select" value={d.modality} onChange={(e) => set("modality", e.target.value)}>
									<option value="">Presencial y en Línea</option><option value="presencial">Solo Presencial</option><option value="online">Solo en Línea</option></select>) : null}
							</div>
							{viejas.periodos ? (
								<>
									<div className="wcpMitad" style={{ marginTop: 8 }}>Solo Estos Periodos</div>
									<Casillas todo opciones={((cursos?.periods || []) as any[]).map((p) => ({ id: p.id, label: p.name, sub: "Desde " + dmy(p.start) }))} elegidos={d.periodIds} onChange={(v) => set("periodIds", v)} alto={150} />
								</>
							) : null}
							{viejas.horarios ? (
								<>
									<div className="wcpMitad" style={{ marginTop: 8 }}>Solo Estos Horarios</div>
									<Casillas todo opciones={horarios.map((s) => ({ id: s.id, label: horarioAmPm(s.name, horarioBonito), sub: [s.levelName, s.start ? "Empieza " + dmy(s.start) : "", s.months + (s.months === 1 ? " Mes" : " Meses")].filter(Boolean).join(" · ") }))} elegidos={d.scheduleIds} onChange={(v) => set("scheduleIds", v)} alto={180} />
								</>
							) : null}

							<h6 className="wdSub">Para Quién</h6>
							{toggles([["", "Todos"], ["nuevo", "Solo Nuevos"], ["reingreso", "Solo Reingresos"], ["actual", "Alumnos Actuales"], ["graduado", "Solo Graduados"]], d.audience, (v) => set("audience", v))}
							{d.audience === "reingreso" ? (
								<div className="wdGrid" style={{ marginTop: 8 }}>
									{campo("Meses Fuera Desde", <input className="form-control" type="number" min="0" value={d.monthsOutMin} onChange={(e) => set("monthsOutMin", e.target.value)} />)}
									{campo("Meses Fuera Hasta", <input className="form-control" type="number" min="0" value={d.monthsOutMax} onChange={(e) => set("monthsOutMax", e.target.value)} />)}
								</div>
							) : null}
						</>
					) : null}

					<h6 className="wdSub">Cuándo</h6>
					<div className="wdGrid">
						{campoVarios("Empieza", <FechaHoraDMY value={d.startsAt} onChange={(v) => set("startsAt", v)} />)}
						{campoVarios("Termina", <FechaHoraDMY value={d.endsAt} onChange={(v) => set("endsAt", v)} />)}
					</div>

					<h6 className="wdSub">Más Opciones</h6>
					{otras.length && datos.group !== "inscripcion" ? (
						<div className="wcpLinea">
							<span className="wcpLineaTit">Se Combina Con</span>
							<div className="wdOpciones">
								<button type="button" className={"wdToggle" + (modoComb === "todas" && !elegirComb ? " wdToggleOn" : "")} onClick={() => { setCombinables(otras.map((p) => p.code)); setElegirComb(false); }}>Todas</button>
								<button type="button" className={"wdToggle" + (modoComb === "ninguna" && !elegirComb ? " wdToggleOn" : "")} onClick={() => { setCombinables([]); setElegirComb(false); }}>Ninguna</button>
								<button type="button" className={"wdToggle" + (modoComb === "algunas" || elegirComb ? " wdToggleOn" : "")} onClick={() => setElegirComb(!elegirComb)}>
									{modoComb === "algunas" ? combinables.length + " de " + otras.length : "Elegir"}</button>
							</div>
						</div>
					) : null}
					{otras.length && datos.group !== "inscripcion" && elegirComb ? (
						<Casillas todo opciones={otras.map((p) => ({ id: p.code, label: p.name, sub: (p.scope === "caja" ? "Caja · " : "") + (GRUPOS[p.group] || p.group) + (p.active ? "" : " · Desactivada") }))} elegidos={combinables} onChange={setCombinables} alto={200} />
					) : null}
					<div className="wdGrid" style={{ marginTop: 8 }}>
						{campo("La Da", <select className="form-select" value={datos.givenBy} disabled={d.tipo === "producto"} onChange={(e) => set("givenBy", e.target.value)}>
							<option value="asesora">Asesora</option>{viejas.supervisora ? <option value="supervisora">Solo Supervisora</option> : null}<option value="caja">Caja</option></select>)}
					</div>
					<div style={{ marginTop: 8 }}>{campo("Guía", <textarea className="form-control" rows={2} value={d.conditions} onChange={(e) => set("conditions", e.target.value)} />)}</div>
					{viejas.aprobacion ? <label className="wdCasilla"><input type="checkbox" checked={d.requiresApproval} onChange={(e) => set("requiresApproval", e.target.checked)} /><span>Requiere Aprobación</span></label> : null}
					{viejas.cupos ? (
						<div className="wdGrid" style={{ marginTop: 8 }}>
							{campo("Tope de Usos", <input className="form-control" type="number" min="1" value={d.quotaLimit} onChange={(e) => set("quotaLimit", e.target.value)} />)}
							{campo("Cada", <select className="form-select" value={d.quotaPeriod} onChange={(e) => set("quotaPeriod", e.target.value)}>
								<option value="">-</option>{Object.keys(PERIODOS).map((k) => <option key={k} value={k}>{PERIODOS[k]}</option>)}</select>)}
							{campo("Contado", <select className="form-select" value={d.quotaScope} onChange={(e) => set("quotaScope", e.target.value)}>
								<option value="plantel">Por Plantel</option><option value="compartido">Compartido Entre Planteles</option></select>)}
							{d.quotaScope === "compartido" ? campo("Llave del Cupo Compartido", <input className="form-control" value={d.quotaKey} onChange={(e) => set("quotaKey", e.target.value)} />) : null}
							{campo("Tope por Asesora", <input className="form-control" type="number" min="1" value={d.quotaPerAdvisor} onChange={(e) => set("quotaPerAdvisor", e.target.value)} />)}
						</div>
					) : null}
					{viejas.dias ? (
						<div className="wdOpciones" style={{ marginTop: 8 }}>
							<span className="wdTenue" style={{ fontSize: 13 }}>Solo Estos Días:</span>
							{DIAS.map(([n, l]) => (
								<button key={"d" + n} type="button" className={"wdToggle wdToggleDia" + (d.weekdays.includes(n) ? " wdToggleOn" : "")}
									onClick={() => set("weekdays", d.weekdays.includes(n) ? d.weekdays.filter((x) => x !== n) : [...d.weekdays, n].sort())}>{l}</button>
							))}
						</div>
					) : null}
					{viejas.franja ? (
						<div className="wdGrid" style={{ marginTop: 6 }}>
							{campoVarios("Franja Desde", <HoraAmPm value={d.timeFrom} onChange={(v) => set("timeFrom", v)} />)}
							{campoVarios("Franja Hasta", <HoraAmPm value={d.timeTo} onChange={(v) => set("timeTo", v)} />)}
						</div>
					) : null}
					{error ? <div className="wdAviso">{error}</div> : null}
				</Modal.Body>
				<Modal.Footer className="wcpPie">
					{promo ? <input className="form-control form-control-sm wcpMotivo" value={d.reason} placeholder="Por Qué Cambia" onChange={(e) => set("reason", e.target.value)} /> : null}
					{!valido ? <span className="wcpFalta">Falta: {faltan.join(" · ")}</span> : <span style={{ flex: 1 }} />}
					<button type="button" className="btn btn-outline-secondary btn-sm" disabled={busy} onClick={onCancel}>Cancelar</button>
					<button type="button" className="btn btn-primary btn-sm" disabled={busy || !valido} onClick={save}>{busy ? "Guardando…" : d.tipo === "referido" && d.mitad === "ambas" && Number(d.value) > 0 && Number(d.valueRefiere) > 0 && !refiereExistente ? "Guardar las Dos Mitades" : "Guardar"}</button>
				</Modal.Footer>
			</Modal>
		);
	};

/** Copiar una promo a otros planteles: los cursos se buscan por nombre allá; lo que no existe se reporta. */
export const CopiarPromo: React.FC<{ campusId: number; promo: Promo; campuses: any[]; onDone: () => void }> = ({ campusId, promo, campuses, onDone }) => {
	const [destinos, setDestinos] = useState<number[]>([]);
	const [res, setRes] = useState<any[] | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [busy, setBusy] = useState(false);
	const opciones = campuses.filter((c) => c.id !== campusId && c.canEdit);
	const unaVez = useUnaALaVez();
	const copiar = () => unaVez(copiarAhora);
	const copiarAhora = async () => {
		try {
			setBusy(true); setError(null);
			const r = await http.paymentService.copyPromotion(campusId, promo.id, destinos);
			setRes(r.data.results || []);
			onDone();
		} catch (e) {
			setError(serverError(e));
		} finally {
			setBusy(false);
		}
	};
	return (
		<div className="wcpCopiar">
			<Casillas todo opciones={opciones.map((c) => ({ id: c.id, label: c.name }))} elegidos={destinos} onChange={setDestinos} alto={180} />
			{res ? res.map((r: any) => {
				const c = campuses.find((x) => x.id === r.campusId);
				return <div key={"r" + r.campusId} className={r.ok ? "wdNota" : "wdAviso"} style={{ fontSize: 13 }}>{c?.name || r.campusId}: {r.ok ? "Copiada" : r.why}
					{r.notCopied && r.notCopied.length ? " · No Existen Allá: " + r.notCopied.join(", ") : ""}{r.note ? " · " + r.note : ""}</div>;
			}) : null}
			{error ? <div className="wdAviso">{error}</div> : null}
			<div className="wdBotones">
				<button type="button" className="btn btn-primary btn-sm" disabled={busy || !destinos.length} onClick={copiar}>{busy ? "Copiando…" : "Copiar"}</button>
			</div>
		</div>
	);
};

export default EditorPromo;
